import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { H as HelpTip } from '../HelpTip/HelpTip-CBpdtpZr.js';
import { f as formGroupRowStyleMixin } from '../FormGroup/formGroupRowStyleMixin-DAlbntir.js';
import { f as formGroupColumnStyleMixin } from '../FormGroup/formGroupColumnStyleMixin-BV9VZTKA.js';
import { I as InputLabel } from '../InputLabel/InputLabel-B_q9Z4ae.js';
import { V as ValidationLine } from '../FormGroup/ValidationLine-BrLY1QIO.js';
import { i as inputStyles } from '../Input/Input.styles-CUQz_INu.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { f as focusOutline } from '../theme/style-mixins/FocusIndicator/focusOutline-oDdaDZ4N.js';
import { i as isString } from '../LocalModules/storm-ui-utils/dist/esm/isString-D4CFRAVS-D4CFRAVS.js';
import { c as createPseudoUID } from '../LocalModules/storm-ui-utils/dist/esm/createPseudoUID-DIFqwkk_-Cdz2nMp1.js';
import { a as TaktIdConsumer } from '../TaktIdContext/TaktIdContext-1m8IJOfp.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-DaCk9T1Y.js';
import { i as isFunction } from '../LocalModules/storm-ui-utils/dist/esm/isFunction-DjxTmSal-DjxTmSal.js';
import { g as getFirstFocusableElement } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFirstFocusableElement-DegkMO1c-COahU-b6.js';
import { I as InlineAlert } from '../InlineAlert/InlineAlert-c5UFTCEp.js';

const styleTextAreaMixin = /*#__PURE__*/css(["", " padding-block:", ";padding-inline:", ";"], inputStyles, ({
  theme
}) => theme.controls.paddingBlock.base.default, ({
  theme
}) => theme.controls.paddingInline.large.default);
const styleTextArea = /*#__PURE__*/css(["", " background-color:", ";border:", ";box-shadow:", " inset;box-sizing:border-box;width:100%;min-width:", ";max-width:100%;border-radius:", ";display:flex;", " ", " ", " ", ";:focus-within{", "}"], styleTextAreaMixin, ({
  theme
}) => theme.input.backgroundColor.default, ({
  theme
}) => theme.border.medium.default, ({
  theme
}) => theme.input.innerShadow, ({
  theme
}) => `min(100%, ${theme.textarea.minWidth})`, ({
  theme
}) => theme.border.radius.medium, ({
  $statusType
}) => $statusType === 'error' && css(["border:", ";"], ({
  theme
}) => theme.support.border.error), ({
  $statusType
}) => $statusType === 'warning' && css(["border:", ";"], ({
  theme
}) => theme.support.border.warning), ({
  $statusType
}) => $statusType === 'success' && css(["border:", ";"], ({
  theme
}) => theme.support.border.success), ({
  disabled
}) => disabled && css(["background-color:", ";color:", ";border:", ";box-shadow:none;"], ({
  theme
}) => theme.input.backgroundColor.unavailable, ({
  theme
}) => theme.text.color.unavailable, ({
  theme
}) => theme.border.medium.unavailable), focusOutline);
const InputGroup = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40013943__sc-1ds8w60-0"
})(["> textarea{", "}"], styleTextArea);
InputGroup.displayName = 'InputGroup';
const TextAreaValidationMessage = /*#__PURE__*/styled(InlineAlert).withConfig({
  componentId: "sc-storm-ui-40013943__sc-1ds8w60-1"
})(["margin-block-start:0;margin-block-end:0;"]);
TextAreaValidationMessage.displayName = 'TextAreaValidationMessage';
const StyledTextarea = /*#__PURE__*/styled.textarea.withConfig({
  componentId: "sc-storm-ui-40013943__sc-1ds8w60-2"
})(["", ""], styleTextArea);
const TextAreaFormGroupComponent = props => {
  const {
    id,
    className,
    disabled,
    label,
    message,
    messageE2EId,
    statusType,
    fullWidth,
    // used in formGroupColumnStyleMixin
    inline,
    hideLabel,
    renderLabelStart,
    renderLabelEnd,
    inputRef,
    help,
    value,
    taktId,
    taktValue,
    ...rest
  } = props;
  const inputGroup = useRef(null);
  const ephemeralId = useMemo(() => {
    if (!isString(id)) {
      return createPseudoUID();
    }
    return undefined;
  }, [id]);
  const renderLabelEndWithHelptip = () => /*#__PURE__*/React__default.createElement(React__default.Fragment, null, isFunction(renderLabelEnd) && renderLabelEnd(), /*#__PURE__*/React__default.createElement(HelpTip, {
    position: "right"
  }, help));
  const focusInput = () => {
    if (inputGroup.current) {
      const focusable = getFirstFocusableElement(inputGroup.current);
      if (focusable instanceof HTMLElement && isFunction(focusable.focus)) {
        focusable.focus();
      }
    }
  };
  const labelId = isString(id) ? undefined : `${ephemeralId}-textarea-label`;
  const messageId = isString(id) ? `${id}-textarea-message` : `${ephemeralId}-textarea-message`;
  const propsForTextarea = {
    ...rest,
    /**
    * When `id` prop is provided, we do not need to do the labelledby work around
    */
    ...(isString(id) ? {
      id
    } : {}),
    ...(isString(labelId) ? {
      'aria-labelledby': labelId
    } : {}),
    /**
    * Add a 'aria-describedby' when any message is applied to the textarea
    */
    ...(message ? {
      'aria-describedby': messageId
    } : {}),
    /*
    * Error get a `'aria-invalid="true"`
    */
    ...(statusType === 'error' ? {
      'aria-invalid': true
    } : {}),
    value,
    ref: inputRef,
    disabled,
    $statusType: statusType
  };
  return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('text-area-form-group')
  }, ({
    getDataTaktAttributes
  }) => /*#__PURE__*/React__default.createElement("div", {
    className: className
  }, label && /*#__PURE__*/React__default.createElement(InputLabel, _extends({
    hidden: hideLabel
  }, getDataTaktAttributes({
    taktIdSuffix: 'label'
  }), {
    renderLabelStart: renderLabelStart,
    renderLabelEnd:
    /**
     * Support the `help` prop by folding it into renderLabelEnd
     */
    help ? renderLabelEndWithHelptip : renderLabelEnd
  }, isString(id) ? {
    htmlFor: id
  } : {
    id: labelId,
    onLabelClick: focusInput
  }), label), /*#__PURE__*/React__default.createElement(InputGroup, {
    disabled: disabled,
    $statusType: statusType,
    ref: inputGroup
  }, /*#__PURE__*/React__default.createElement(StyledTextarea, _extends({}, getDataTaktAttributes(), propsForTextarea))), !!message && /*#__PURE__*/React__default.createElement(ValidationLine, _extends({
    role: "alert",
    "aria-live": "assertive",
    id: messageId
  }, getDataTaktAttributes({
    taktIdSuffix: 'message'
  })), typeof message === 'function' ? message(value) : /*#__PURE__*/React__default.createElement(TextAreaValidationMessage, {
    variant: statusType,
    message: message,
    "data-e2e-id": messageE2EId
  }))));
};
const TextAreaFormGroup = /*#__PURE__*/styled(TextAreaFormGroupComponent).withConfig({
  componentId: "sc-storm-ui-40013943__sc-1ds8w60-3"
})(["", " ", ""], ({
  inline
}) => inline ? formGroupRowStyleMixin : formGroupColumnStyleMixin, isMobile(formGroupColumnStyleMixin));
TextAreaFormGroup.displayName = 'TextAreaFormGroup';

export { TextAreaFormGroup as T, styleTextAreaMixin as s };
//# sourceMappingURL=TextAreaFormGroup-KCcN6HV3.js.map
